import axios from "axios";

// The CMS is served by THIS website's own backend (same deployment as the site).
// It is completely separate from the SHREDxpro member backend.
const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/cms`;
const TOKEN_KEY = "sx_cms_admin";

export const getAdminToken = () => localStorage.getItem(TOKEN_KEY);
export const setAdminToken = (t) => {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
};

const auth = () => ({ headers: { Authorization: `Bearer ${getAdminToken()}` } });

// Uploaded media comes back as "/api/cms/uploads/..." — prefix the site origin.
export const mediaUrl = (u) =>
    u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;

export const getVisitorId = () => {
    let v = localStorage.getItem("sx_vid");
    if (!v) {
        v = crypto.randomUUID();
        localStorage.setItem("sx_vid", v);
    }
    return v;
};

// ------------------------------------------------------------- public ------
export const fetchCategories = () => axios.get(`${BASE}/categories`).then((r) => r.data.categories);
export const fetchResources = (params = {}) =>
    axios.get(`${BASE}/resources`, { params }).then((r) => r.data.resources);
export const fetchResource = (slug) => axios.get(`${BASE}/resources/${slug}`).then((r) => r.data);
export const trackView = (slug) =>
    axios.post(`${BASE}/resources/${slug}/view`, { visitor_id: getVisitorId() }).catch(() => {});
export const trackCta = (slug, cta) =>
    axios.post(`${BASE}/resources/${slug}/cta`, { visitor_id: getVisitorId(), cta }).catch(() => {});
export const submitLead = (slug, email) =>
    axios.post(`${BASE}/resources/${slug}/lead`, { email }).then((r) => r.data);

// -------------------------------------------------------------- admin ------
export const adminLogin = (email, password) =>
    axios.post(`${BASE}/admin/login`, { email, password }).then((r) => r.data);
export const adminMe = () => axios.get(`${BASE}/admin/me`, auth()).then((r) => r.data);
export const adminChangePassword = (body) =>
    axios.post(`${BASE}/admin/change-password`, body, auth()).then((r) => r.data);

export const adminListResources = (status = "") =>
    axios.get(`${BASE}/admin/resources`, { ...auth(), params: status ? { status } : {} }).then((r) => r.data.resources);
export const adminGetResource = (id) => axios.get(`${BASE}/admin/resources/${id}`, auth()).then((r) => r.data);
export const adminCreateResource = (body) => axios.post(`${BASE}/admin/resources`, body, auth()).then((r) => r.data);
export const adminUpdateResource = (id, body) => axios.put(`${BASE}/admin/resources/${id}`, body, auth()).then((r) => r.data);
export const adminDuplicateResource = (id) =>
    axios.post(`${BASE}/admin/resources/${id}/duplicate`, {}, auth()).then((r) => r.data);
export const adminSetStatus = (id, status) =>
    axios.post(`${BASE}/admin/resources/${id}/status`, { status }, auth()).then((r) => r.data);
export const adminDeleteResource = (id) => axios.delete(`${BASE}/admin/resources/${id}`, auth()).then((r) => r.data);

export const adminListCategories = () => axios.get(`${BASE}/admin/categories`, auth()).then((r) => r.data.categories);
export const adminCreateCategory = (name) => axios.post(`${BASE}/admin/categories`, { name }, auth()).then((r) => r.data);
export const adminUpdateCategory = (id, name) =>
    axios.put(`${BASE}/admin/categories/${id}`, { name }, auth()).then((r) => r.data);
export const adminDeleteCategory = (id) => axios.delete(`${BASE}/admin/categories/${id}`, auth()).then((r) => r.data);

export const adminAnalytics = () => axios.get(`${BASE}/admin/analytics`, auth()).then((r) => r.data);
export const adminLeads = () => axios.get(`${BASE}/admin/leads`, auth()).then((r) => r.data.leads);

export const adminUpload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await axios.post(`${BASE}/admin/upload`, fd, auth());
    return r.data.url;
};

// AI resource builder — transient LLM latency can 502 once; retry a single time.
const withRetry = async (fn) => {
    try {
        return await fn();
    } catch (e) {
        if (e?.response?.status === 502) {
            await new Promise((r) => setTimeout(r, 2000));
            return fn();
        }
        throw e;
    }
};

export const adminAiOutline = (instruction) =>
    withRetry(() => axios.post(`${BASE}/admin/ai/outline`, { instruction }, { ...auth(), timeout: 120000 }).then((r) => r.data));
export const adminAiGenerate = (instruction, sections) =>
    withRetry(() => axios.post(`${BASE}/admin/ai/generate`, { instruction, sections }, { ...auth(), timeout: 240000 }).then((r) => r.data));
export const adminAiAssist = (instruction, block, context) =>
    withRetry(() => axios.post(`${BASE}/admin/ai/assist`, { instruction, block, context }, { ...auth(), timeout: 120000 }).then((r) => r.data));
